import {useMemo} from 'react';

import canOpenFile from 'file-browser/canOpenFile';
import {ChatArtifactKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ArtifactType, getArtifactTypeLabel} from 'llm/chat-library/ArtifactTypes';
import type {ChatSidebarData} from 'llm/chat-library/useMessageThread';
import type {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import APIUtil, {apiIdToIconName} from 'util/APIUtil';
import {assertNever} from 'util/Types';

/** The display ArtifactType for a produced-artifact kind, used for the label when the backend has no name. */
function artifactTypeForKind(
  kind: ChatArtifactKind.DRAFT_DOCUMENT | ChatArtifactKind.ACTIVE_CANVAS
): ArtifactType.DOCUMENT | ArtifactType.CANVAS {
  return kind === ChatArtifactKind.ACTIVE_CANVAS ? ArtifactType.CANVAS : ArtifactType.DOCUMENT;
}

export type SidebarSection = 'inputs' | 'outputs';

/**
 * A single item shown in the right sidebar's list (LLM_ENABLE_RIGHT_SIDEBAR).
 *
 * Every item carries the identifiers and display metadata the backend returned. Rows describe what
 * the session touched or produced; resolving an artifact row to its live editor is the detail view's
 * job, keyed by this id (see RightSidebarLayout).
 */
interface BaseSidebarItem {
  /**
   * The backend externalId of the underlying input, artifact, or affected object. Inputs and outputs
   * are disjoint by externalId (see hydrate_affected_objects, which excludes user uploads), so the id
   * is unique across both sections. The chat thread's draft-document button and canvas card deep-link
   * to an item's detail view using this same externalId (see Chat.tsx).
   */
  id: string;
  label: string;
  icon: BenchlingIconName;
  section: SidebarSection;
  /**
   * The audited action that produced this output (e.g. "Created", "Edited"), shown as a subtitle in
   * the outputs list. Absent for inputs and for outputs the backend did not attribute to an action.
   */
  action?: string;
  /**
   * ISO-8601 timestamp of the output's latest action, rendered as a relative time beneath its name.
   * Absent for inputs and untimed outputs.
   */
  lastTouchedAt?: string;
}

/** Picks the list icon for an output row from its audited action. */
function iconForAction(action: string | null): BenchlingIconName {
  switch (action?.toLowerCase()) {
    case 'created':
      return 'plus';
    case 'edited':
    case 'updated':
      return 'pen';
    default:
      return 'document';
  }
}

export interface ArtifactSidebarItem extends BaseSidebarItem {
  kind: 'artifact';
  /**
   * Which interactive artifact this row is, so the detail view knows which live editor to render for
   * it. The backend owns that the artifact exists; the detail view resolves it to the streamed data
   * by matching this row's id (see RightSidebarLayout).
   */
  artifactType: ArtifactType.DOCUMENT | ArtifactType.CANVAS;
}

export interface FileSidebarItem extends BaseSidebarItem {
  kind: 'file';
  chatSessionId: string;
  externalFileId: string;
  filename: string;
  mimeType: string;
}

export interface ChartSidebarItem extends BaseSidebarItem {
  kind: 'chart';
  chartId: string;
}

export interface AffectedObjectSidebarItem extends BaseSidebarItem {
  kind: 'affected-object';
  externalId: string;
  objectType: string;
  /**
   * Whether the app can open this object. Agents have a dedicated homepage; editor-backed objects,
   * folders, and chat sessions open through the file browser. Other Benchling objects and objects
   * from external systems are non-interactive.
   */
  openable: boolean;
}

export type PreviewableSidebarItem = ArtifactSidebarItem | ChartSidebarItem | FileSidebarItem;
export type SidebarItem = PreviewableSidebarItem | AffectedObjectSidebarItem;

export interface UseSidebarItemsOptions {
  /** Canonical inputs, artifacts, and affected objects returned with the chat session. */
  sidebarData: ChatSidebarData | null;
}

export interface UseSidebarItemsResult {
  /** The ordered items to show in the list. */
  items: Array<SidebarItem>;
}

/**
 * Provides the items shown in the right sidebar's list.
 *
 * The backend is the single source of truth for which inputs, artifacts, and outputs belong to the
 * session, and for their identity and labels. This hook is a pure transform of that data into list
 * rows; it does not touch chat application state. Resolving an interactive artifact row to its live
 * editor is the detail view's job, keyed by the row's id (see RightSidebarLayout).
 * Selection/open state lives in the shared AI panel state so controls outside the layout can drive it.
 */
export function useSidebarItems({sidebarData}: UseSidebarItemsOptions): UseSidebarItemsResult {
  const items = useMemo<Array<SidebarItem>>(() => {
    if (sidebarData == null) {
      return [];
    }

    const sidebarItems: Array<SidebarItem> = sidebarData.inputFiles.map((file) => ({
      kind: 'file',
      section: 'inputs',
      id: file.externalId,
      label: file.filename,
      icon: 'file',
      chatSessionId: sidebarData.chatSessionId,
      externalFileId: file.externalId,
      filename: file.filename,
      mimeType: file.mediaType,
    }));

    // The backend returns everything the chat touched or produced as a single affectedObjects list.
    // Each entry's artifactKind distinguishes an artifact the chat produced (non-null) from a plain
    // affected object (null); all of them are shown together under outputs.
    // Audited outputs arrive most-recently-touched first, followed by unaudited artifacts in
    // insertion order. Reverse only the dated outputs so new items append within either group.
    const orderedAffectedObjects = sidebarData.affectedObjects
      .filter((object) => object.lastTouchedAt != null)
      .reverse()
      .concat(sidebarData.affectedObjects.filter((object) => object.lastTouchedAt == null));
    for (const object of orderedAffectedObjects) {
      switch (object.artifactKind) {
        case ChatArtifactKind.DRAFT_DOCUMENT:
        case ChatArtifactKind.ACTIVE_CANVAS:
          // The backend owns that this artifact exists and where it sorts. The row records which
          // artifact type it is; the detail view resolves it to its live editor by matching this id.
          if (object.externalId != null) {
            sidebarItems.push({
              kind: 'artifact',
              section: 'outputs',
              id: object.externalId,
              label: object.objectName ?? getArtifactTypeLabel(artifactTypeForKind(object.artifactKind)),
              icon: iconForAction(object.latestAction),
              action: object.latestAction ?? undefined,
              lastTouchedAt: object.lastTouchedAt ?? undefined,
              artifactType: artifactTypeForKind(object.artifactKind),
            });
          }
          break;
        case ChatArtifactKind.FILE:
          if (object.externalId != null && object.objectName != null && object.mediaType != null) {
            sidebarItems.push({
              kind: 'file',
              section: 'outputs',
              id: object.externalId,
              label: object.objectName,
              icon: iconForAction(object.latestAction),
              action: object.latestAction ?? undefined,
              lastTouchedAt: object.lastTouchedAt ?? undefined,
              chatSessionId: sidebarData.chatSessionId,
              externalFileId: object.externalId,
              filename: object.objectName,
              mimeType: object.mediaType,
            });
          }
          break;
        case ChatArtifactKind.ANALYSIS_PLAN:
          // Analysis chats retain AnalysisWorkspaceLayout in this rollout.
          break;
        case null: {
          // A null artifactKind means a plain affected object the chat touched, shown under outputs.
          if (object.externalId == null) {
            break;
          }
          if (APIUtil.isChart(object.externalId)) {
            sidebarItems.push({
              kind: 'chart',
              section: 'outputs',
              id: object.externalId,
              label: object.objectName ?? 'Chart',
              icon: apiIdToIconName(object.externalId),
              action: object.latestAction ?? undefined,
              lastTouchedAt: object.lastTouchedAt ?? undefined,
              chartId: object.externalId,
            });
            break;
          }
          // The non-throwing lookup returns null for external systems' ids.
          const modelType = APIUtil.apiIdToMaybeType(object.externalId);
          sidebarItems.push({
            kind: 'affected-object',
            section: 'outputs',
            id: object.externalId,
            label: object.objectName ?? object.objectType ?? object.externalId,
            // A known model type gets its own icon (e.g. a DNA sequence shows the DNA icon); an
            // external system's object falls back to an icon based on the audited action.
            icon: modelType != null ? apiIdToIconName(object.externalId) : iconForAction(object.latestAction),
            action: object.latestAction ?? undefined,
            lastTouchedAt: object.lastTouchedAt ?? undefined,
            externalId: object.externalId,
            objectType: object.objectType ?? '',
            // Agents have a dedicated homepage. Editor-backed objects, folders, and chat sessions
            // open through the file browser. Other objects, such as dropdowns, are managed elsewhere
            // (e.g. settings pages) and remain non-interactive.
            openable: APIUtil.isRoutine(object.externalId) || canOpenFile(object.externalId),
          });
          break;
        }
        default:
          assertNever(object.artifactKind);
      }
    }

    return sidebarItems;
  }, [sidebarData]);

  return {items};
}
